/**
 * `next/navigation` compatibility shim.
 *
 * Re-exports the subset of Next's App Router hooks the webapp uses, backed by
 * react-router-dom. The shape — `useRouter().push(url)`, `usePathname()`,
 * `useSearchParams()`, `useParams()`, `redirect()`, `notFound()` — matches
 * what the migrated files already call.
 */
import {
  useNavigate,
  useLocation,
  useParams as useRRParams,
  useSearchParams as useRRSearchParams,
} from 'react-router-dom';

export interface AppRouterInstance {
  push(href: string, options?: { scroll?: boolean }): void;
  replace(href: string, options?: { scroll?: boolean }): void;
  back(): void;
  forward(): void;
  refresh(): void;
  prefetch(href: string): void;
}

export function useRouter(): AppRouterInstance {
  const navigate = useNavigate();
  return {
    push: (href: string) => {
      navigate(href);
    },
    replace: (href: string) => {
      navigate(href, { replace: true });
    },
    back: () => {
      navigate(-1);
    },
    forward: () => {
      navigate(1);
    },
    refresh: () => {
      // App Router's refresh re-runs server components. The Jest client is a
      // pure SPA, so the closest equivalent is a hard reload — but most
      // callers actually want a re-fetch, not a reload, so we no-op here and
      // expect callers to refetch their own data when appropriate.
    },
    prefetch: () => {
      // No router-level prefetch in react-router-dom; safe no-op.
    },
  };
}

export function usePathname(): string {
  return useLocation().pathname;
}

export function useSearchParams(): URLSearchParams {
  const [searchParams] = useRRSearchParams();
  return searchParams;
}

export function useParams<T extends Record<string, string | string[]> = Record<string, string | string[]>>(): T {
  return useRRParams() as unknown as T;
}

/**
 * Imperative redirect — Next.js style. Used outside of components or as a
 * thrown navigation. We render via `window.location.assign` so this works
 * during event handlers and async code paths regardless of router context.
 */
export function redirect(href: string): never {
  window.location.assign(href);
  // The throw matches Next's contract: callers expect this function to never
  // return. The runtime navigation above will unmount the React tree.
  throw new Error(`NEXT_REDIRECT: ${href}`);
}

export function permanentRedirect(href: string): never {
  return redirect(href);
}

/**
 * Mirrors `notFound()` from `next/navigation`. The webapp uses this in client
 * components to short-circuit into Next's not-found UI. Here we route to a
 * conventional /404 path; route definitions can map that to a NotFound page.
 */
export function notFound(): never {
  window.location.assign('/404');
  throw new Error('NEXT_NOT_FOUND');
}

// Selected layout segment hooks — Next exposes these but the webapp doesn't
// use them yet. Exported as no-op shims so any future migration doesn't break.
export function useSelectedLayoutSegment(): string | null {
  return null;
}

export function useSelectedLayoutSegments(): string[] {
  return [];
}
